import { Vector3 } from 'three';
import { AIR_CAP } from './constants';

/**
 * Pure Quake/Source movement math. No scene or renderer dependencies — every
 * function here is unit-tested and shared by the fly camera (Phase 0) and the
 * real surf controller (Phase 1+). Velocity vectors are mutated in place and
 * returned, Three.js style.
 */

/**
 * Quake PM_Accelerate: only the projection of velocity onto wishDir is limited,
 * which is what makes strafe-based speed gain possible.
 */
export function accelerate(
  velocity: Vector3,
  wishDir: Vector3,
  wishSpeed: number,
  accel: number,
  dt: number,
): Vector3 {
  const currentSpeed = velocity.dot(wishDir);
  const addSpeed = wishSpeed - currentSpeed;
  if (addSpeed <= 0) {
    return velocity;
  }
  const accelSpeed = Math.min(accel * dt * wishSpeed, addSpeed);
  return velocity.addScaledVector(wishDir, accelSpeed);
}

/**
 * Source PM_AirAccelerate: identical to accelerate() except the target speed
 * along wishDir is capped at `cap` (AIR_CAP, 30 u/s), while the acceleration
 * rate still scales with the uncapped wishSpeed.
 */
export function airAccelerate(
  velocity: Vector3,
  wishDir: Vector3,
  wishSpeed: number,
  accel: number,
  dt: number,
  cap: number = AIR_CAP,
): Vector3 {
  const cappedSpeed = Math.min(wishSpeed, cap);
  const currentSpeed = velocity.dot(wishDir);
  const addSpeed = cappedSpeed - currentSpeed;
  if (addSpeed <= 0) {
    return velocity;
  }
  const accelSpeed = Math.min(accel * dt * wishSpeed, addSpeed);
  return velocity.addScaledVector(wishDir, accelSpeed);
}

/**
 * Quake PM_ClipVelocity: remove the velocity component pointing into a plane.
 * Applied each tick against a steep ramp plane (normal.y < SURF_NORMAL_Y),
 * this IS surfing — gravity keeps adding speed, the plane keeps redirecting it.
 */
export function clipVelocity(velocity: Vector3, normal: Vector3, overbounce: number): Vector3 {
  const backoff = velocity.dot(normal) * overbounce;
  return velocity.addScaledVector(normal, -backoff);
}

/** Ground friction with the Source stop-speed floor. Never call while airborne. */
export function applyFriction(
  velocity: Vector3,
  friction: number,
  stopSpeed: number,
  dt: number,
): Vector3 {
  const speed = velocity.length();
  if (speed < 1e-6) {
    return velocity.set(0, 0, 0);
  }
  const control = Math.max(speed, stopSpeed);
  const drop = control * friction * dt;
  const newSpeed = Math.max(speed - drop, 0);
  return velocity.multiplyScalar(newSpeed / speed);
}

/**
 * Horizontal wish direction from input axes and view yaw (radians).
 * forwardInput: +1 forward / -1 back. rightInput: +1 right / -1 left.
 * At yaw 0 the player faces -Z; yaw increases turning left (Three.js convention).
 */
export function wishDirFromInput(forwardInput: number, rightInput: number, yaw: number): Vector3 {
  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  // forward = (-sin, 0, -cos), right = (cos, 0, -sin)
  const x = -sin * forwardInput + cos * rightInput;
  const z = -cos * forwardInput - sin * rightInput;
  const dir = new Vector3(x, 0, z);
  const len = dir.length();
  return len > 1e-6 ? dir.divideScalar(len) : dir.set(0, 0, 0);
}
